import { memo, useCallback, useRef, useState, useEffect } from 'react';
import useLocale from 'next-translate/useTranslation';
import { ClickToAnnotateProps } from './types';
import { useAnnotationState } from './hooks/useAnnotationState';
import { compileAnnotationsToPrompt } from './utils/promptCompiler';
import MarkerPin from './components/MarkerPin';
import MarkerInput from './components/MarkerInput';
import AnnotationToolbar from './components/AnnotationToolbar';

function ClickToAnnotate({
  imageUrl,
  onComplete,
  onCancel,
  className = '',
  isGenerating = false,
  embedded = false,
  onMarkersChange,
  imageMaxHeight,
}: ClickToAnnotateProps) {
  const { t } = useLocale('ai-images');
  const containerRef = useRef<HTMLDivElement>(null);
  const [containerRect, setContainerRect] = useState<DOMRect | null>(null);
  const [imageLoaded, setImageLoaded] = useState(false);

  const {
    markers,
    activeMarkerId,
    setActiveMarkerId,
    addMarker,
    updateMarkerDescription,
    moveMarker,
    deleteMarker,
    clearAll,
    undo,
    redo,
    canUndo,
    canRedo,
  } = useAnnotationState();

  // Update container rect on resize
  useEffect(() => {
    const updateRect = () => {
      if (containerRef.current) {
        setContainerRect(containerRef.current.getBoundingClientRect());
      }
    };

    updateRect();
    window.addEventListener('resize', updateRect);
    return () => window.removeEventListener('resize', updateRect);
  }, [imageLoaded]);

  // Notify parent when markers change (for embedded mode)
  useEffect(() => {
    if (embedded && onMarkersChange) {
      const hasValid = markers.some((m) => m.description.trim());
      onMarkersChange(markers, hasValid);
    }
  }, [markers, embedded, onMarkersChange]);

  // Handle keyboard shortcuts
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (activeMarkerId) {
          setActiveMarkerId(null);
        } else if (!embedded) {
          onCancel();
        }
      } else if (e.key === 'Delete' || e.key === 'Backspace') {
        if (activeMarkerId && !isInputFocused()) {
          deleteMarker(activeMarkerId);
        }
      } else if (e.key === 'z' && (e.metaKey || e.ctrlKey)) {
        if (e.shiftKey) {
          redo();
        } else {
          undo();
        }
        e.preventDefault();
      }
    };

    const isInputFocused = () => {
      const activeElement = document.activeElement;
      return (
        activeElement?.tagName === 'INPUT' ||
        activeElement?.tagName === 'TEXTAREA'
      );
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [
    activeMarkerId,
    deleteMarker,
    onCancel,
    redo,
    setActiveMarkerId,
    undo,
    embedded,
  ]);

  const handleImageClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (isGenerating) return;

      const container = containerRef.current;
      if (!container) return;

      const rect = container.getBoundingClientRect();
      const x = ((e.clientX - rect.left) / rect.width) * 100;
      const y = ((e.clientY - rect.top) / rect.height) * 100;

      // Clamp values
      const clampedX = Math.max(0, Math.min(100, x));
      const clampedY = Math.max(0, Math.min(100, y));

      addMarker(clampedX, clampedY);
    },
    [addMarker, isGenerating],
  );

  const handleDone = useCallback(() => {
    const compiledPrompt = compileAnnotationsToPrompt(markers);
    if (compiledPrompt) {
      onComplete(compiledPrompt);
    }
  }, [markers, onComplete]);

  const activeMarker = markers.find((m) => m.id === activeMarkerId);

  // Check if any marker has description
  const hasValidAnnotations = markers.some((m) => m.description.trim());

  return (
    <div
      className={`relative w-full h-full overflow-hidden flex flex-col sm:block ${className}`}
    >
      {/* Toolbar - in flow on mobile, absolute overlay on desktop */}
      <AnnotationToolbar
        markerCount={markers.length}
        hasValidAnnotations={hasValidAnnotations}
        onClearAll={clearAll}
        onUndo={undo}
        onRedo={redo}
        onCancel={onCancel}
        onDone={handleDone}
        canUndo={canUndo}
        canRedo={canRedo}
        isGenerating={isGenerating}
        embedded={embedded}
      />

      {/* Help text - in flow on mobile (above image), absolute overlay on desktop */}
      {markers.length === 0 && !isGenerating && (
        <div className='flex justify-center py-2 sm:py-0 sm:absolute sm:bottom-6 sm:left-1/2 sm:-translate-x-1/2 z-10'>
          <div className='bg-white/95 dark:bg-slate-800/95 backdrop-blur-md rounded-full px-4 py-2 shadow-lg border border-gray-200 dark:border-gray-700'>
            <p className='text-sm text-gray-600 dark:text-gray-300'>
              {t('Click anywhere on the image to add a marker')}
            </p>
          </div>
        </div>
      )}

      {/* Image container with click handling */}
      <div className='flex-1 min-h-0 w-full sm:h-full flex items-center justify-center'>
        <div
          ref={containerRef}
          onClick={handleImageClick}
          className='relative cursor-crosshair select-none'
          style={{ display: 'inline-block' }}
        >
          {/* Base image */}
          <img
            src={imageUrl}
            alt='Image to annotate'
            className='max-w-full object-contain'
            style={{ maxHeight: imageMaxHeight || 'calc(95vh - 4rem)' }}
            onLoad={() => {
              setImageLoaded(true);
              // Update rect after image loads
              if (containerRef.current) {
                setContainerRect(containerRef.current.getBoundingClientRect());
              }
            }}
            draggable={false}
          />

          {/* Markers */}
          {imageLoaded &&
            markers.map((marker) => (
              <MarkerPin
                key={marker.id}
                marker={marker}
                isActive={marker.id === activeMarkerId}
                containerRef={containerRef}
                onClick={() => setActiveMarkerId(marker.id)}
                onMove={(x, y) => moveMarker(marker.id, x, y)}
                onDelete={() => deleteMarker(marker.id)}
              />
            ))}

          {/* Active marker input */}
          {imageLoaded && activeMarker && (
            <MarkerInput
              marker={activeMarker}
              containerRect={containerRect}
              onChange={(desc) =>
                updateMarkerDescription(activeMarker.id, desc)
              }
              onClose={() => setActiveMarkerId(null)}
              onDelete={() => deleteMarker(activeMarker.id)}
            />
          )}
        </div>
      </div>

      {/* Warning if markers but no descriptions - in flow on mobile (below image), absolute overlay on desktop */}
      {markers.length > 0 && !hasValidAnnotations && !isGenerating && (
        <div className='flex justify-center py-2 sm:py-0 sm:absolute sm:bottom-6 sm:left-1/2 sm:-translate-x-1/2 z-10'>
          <div className='bg-yellow-50 dark:bg-yellow-900/30 backdrop-blur-md rounded-full px-4 py-2 shadow-lg border border-yellow-200 dark:border-yellow-800'>
            <p className='text-sm text-yellow-700 dark:text-yellow-300'>
              {t('Add descriptions to your markers to generate changes')}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

export default memo(ClickToAnnotate);
